/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.15.0
 */

import { Step } from 'react-joyride';

import { EOnboardingComponentsId } from '../types/onboardingTypes';

export type IOnboardingElementsConfiguration = Pick<
    Step,
    'spotlightPadding' | 'placement' | 'isFixed' | 'styles' | 'offset'
> & { flexibleScroll?: boolean };

export const onboardingElementsConfiguration: { [key in EOnboardingComponentsId]: IOnboardingElementsConfiguration } = {
    // Верхнее меню
    [EOnboardingComponentsId.linkAccounts]: {
        offset: -5,
        placement: 'bottom-start',
        spotlightPadding: 0,
        styles: {
            spotlight: {
                marginLeft: '-6px',
            },
        },
    },
    [EOnboardingComponentsId.linkHistoryAndOrders]: {
        offset: -5,
        placement: 'bottom',
        spotlightPadding: 0,
    },
    [EOnboardingComponentsId.linkProducts]: {
        offset: -5,
        placement: 'bottom',
        spotlightPadding: 0,
    },
    [EOnboardingComponentsId.linkSubscribers]: {
        offset: -5,
        placement: 'bottom-start',
        spotlightPadding: 0,
    },
    [EOnboardingComponentsId.notificationsMenu]: {
        offset: -5,
        placement: 'bottom-end',
        spotlightPadding: 0,
    },

    // Главная страница
    [EOnboardingComponentsId.widgetCost]: {
        placement: 'right-start',
        flexibleScroll: true,
    },
    [EOnboardingComponentsId.widgetPersonalAccount]: {
        placement: 'right-start',
        flexibleScroll: true,
    },
    [EOnboardingComponentsId.widgetSearchAccounts]: {
        placement: 'bottom',
        flexibleScroll: true,
    },
    [EOnboardingComponentsId.widgetSubscriberMaxCosts]: {
        placement: 'left',
        flexibleScroll: true,
    },
    [EOnboardingComponentsId.fastActionsPlate]: {
        placement: 'bottom',
        flexibleScroll: true,
    },

    // Расходы ЛС
    [EOnboardingComponentsId.expensesAnalyzerAccountChart]: {
        placement: 'top',
        flexibleScroll: true,
    },
    [EOnboardingComponentsId.expensesAnalyzerAccountChartTab]: {
        placement: 'bottom-end',
    },
    [EOnboardingComponentsId.expensesAnalyzerAccountTable]: {
        placement: 'top',
        flexibleScroll: true,
        styles: {
            spotlight: {
                marginLeft: '10px',
            },
        },
    },
    [EOnboardingComponentsId.expensesAnalyzerAccountTableFooterButtonCallDetails]: {
        placement: 'top',
    },
    [EOnboardingComponentsId.expensesAnalyzerAccountTableFooterButtonShowDailyExpenses]: {
        placement: 'top',
    },
    [EOnboardingComponentsId.expensesAnalyzerAccountTableFooterButtonShowSubsExpenses]: {
        placement: 'top',
    },
    [EOnboardingComponentsId.expensesAnalyzerAccountTableFooterButtonWizardExpensesDetail]: {
        placement: 'top',
    },

    // Список ЛС
    [EOnboardingComponentsId.accountsTableSettings]: {
        placement: 'left',
    },
    [EOnboardingComponentsId.accountsTableFooterButtonBills]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.accountsTableFooterButtonDcRules]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.accountsTableFooterButtonReconciliation]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.accountsTableFooterButtonExpenses]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.accountsTableFooterButtonReports]: {
        isFixed: true,
        placement: 'top-end',
    },

    // Список МС
    [EOnboardingComponentsId.subscriberTable]: {
        placement: 'top',
    },
    [EOnboardingComponentsId.subscriberTableSettings]: {
        placement: 'bottom',
    },
    [EOnboardingComponentsId.subscriberTablePersonalImport]: {
        placement: 'bottom',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonManageBlock]: {
        isFixed: true,
        placement: 'top-start',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonLimit]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonCorporate]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonLabelEdit]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonCallDetails]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonTariffChange]: {
        isFixed: true,
        placement: 'top-start',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonExpenses]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonInquiryCreate]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonAdditionalAccount]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonAccessLevel]: {
        isFixed: true,
        placement: 'top-end',
    },
    [EOnboardingComponentsId.subscriberTableFooterButtonReports]: {
        isFixed: true,
        placement: 'top-end',
    },
    [EOnboardingComponentsId.subscribersInRoaming]: {
        isFixed: true,
        placement: 'top',
    },

    // Тарифы и услуги
    [EOnboardingComponentsId.productBtnConnect]: {
        placement: 'top-end',
        isFixed: true,
    },
    [EOnboardingComponentsId.productCardMooreDetailsLink]: {
        placement: 'top',
        spotlightPadding: 5,
        offset: 0,
    },
    [EOnboardingComponentsId.productCardList]: {
        placement: 'top',
        spotlightPadding: 0,
        offset: -5,
        flexibleScroll: true,
    },
    [EOnboardingComponentsId.productCategories]: {
        placement: 'right',
        spotlightPadding: 0,
        offset: -5,
        flexibleScroll: true,
    },
    [EOnboardingComponentsId.productFilterRegion]: {
        placement: 'bottom-end',
    },
    [EOnboardingComponentsId.productTabSelected]: {
        placement: 'bottom-start',
    },

    // Настройки
    [EOnboardingComponentsId.settingsTabLabels]: {
        isFixed: true,
        placement: 'right',
        spotlightPadding: 0,
        offset: -5,
    },
    [EOnboardingComponentsId.settingsTabPositions]: {
        isFixed: true,
        placement: 'right',
        spotlightPadding: 0,
        offset: -5,
    },
    [EOnboardingComponentsId.settingsTabProfile]: {
        isFixed: true,
        placement: 'right',
        spotlightPadding: 0,
        offset: -5,
    },
    [EOnboardingComponentsId.settingsTabNotifications]: {
        isFixed: true,
        placement: 'right',
        spotlightPadding: 0,
        offset: -5,
    },

    // Счета
    [EOnboardingComponentsId.billsTable]: {
        placement: 'top',
        flexibleScroll: true,
        offset: -5,
    },
    [EOnboardingComponentsId.billsDescription]: {
        placement: 'bottom',
    },
    [EOnboardingComponentsId.billsTableFooterButtonBillsDoc]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.billsTableFooterButtonCompare]: {
        isFixed: true,
        placement: 'top',
    },
    [EOnboardingComponentsId.billsTableFooterButtonReconciliation]: {
        isFixed: true,
        placement: 'top',
    },

    // Обращения
    [EOnboardingComponentsId.inquiriesTabMenu]: {
        placement: 'top-end',
        flexibleScroll: true,
    },
};
